import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ToastContext } from "../../../../hooks/context";
import {
  defaultDateDisplay,
  formatCurrency,
  printStandardInvoice,
} from "../../../../helpers";
import { useSendOrderReceiptMutation } from "../../../../hooks/api/mutations/useOrder";
import { useGenerateInvoice } from "../../../../hooks/api/mutations/useInventory";
import { useGetInvoiceBankAccounts } from "../../../../hooks/api/queries/useAccountings";

const getCustomerEmail = (details) => details?.customer?.email || "";

const getCustomerPhone = (details) => details?.customer?.phone || "";

const formatWhatsAppPhone = (phoneInput, countryCode = "234") => {
  const digits = (phoneInput || "").replace(/\D/g, "");
  if (!digits) return "";
  const code = (countryCode || "234").replace(/\D/g, "");
  if (digits.startsWith("0")) return `${code}${digits.slice(1)}`;
  if (digits.length === 10 && !digits.startsWith(code))
    return `${code}${digits}`;
  return digits;
};

export const useInvoiceReady = ({ orderDetails, invoiceUrl, closeModal }) => {
  const triggerToast = useContext(ToastContext);
  const sendOrderReceiptMutation = useSendOrderReceiptMutation();
  const {
    handlegenerateInvoice: triggerGenerateInvoice,
    isLoading: isGeneratingInvoice,
  } = useGenerateInvoice();

  const { business_phone_code } = useSelector(
    (state) => state.profileDetailsReducer?.businessDetails || {},
  );

  const storeId = orderDetails?.store?.id || orderDetails?.store_id;

  const storeDefaultBankAccount = useMemo(
    () => orderDetails?.store?.default_bank_account || null,
    [orderDetails],
  );

  const [selectedBankAccountId, setSelectedBankAccountId] = useState(null);

  useEffect(() => {
    setSelectedBankAccountId(null);
  }, [orderDetails?.id]);

  const { data: bankAccountsData, isLoading: isLoadingBankAccounts } =
    useGetInvoiceBankAccounts({
      store_id: storeId,
      isEnabled: true,
    });

  const bankAccounts = useMemo(
    () => bankAccountsData?.data || [],
    [bankAccountsData],
  );

  const activeBankAccount = useMemo(() => {
    if (selectedBankAccountId != null) {
      return (
        bankAccounts.find((a) => a.id === selectedBankAccountId) ||
        storeDefaultBankAccount
      );
    }
    return storeDefaultBankAccount;
  }, [selectedBankAccountId, bankAccounts, storeDefaultBankAccount]);

  const bankAccountIdToSend = selectedBankAccountId ?? undefined;
  const [expandedAction, setExpandedAction] = useState(null);
  const [emailInput, setEmailInput] = useState(() =>
    getCustomerEmail(orderDetails),
  );
  const [phoneInput, setPhoneInput] = useState(() =>
    getCustomerPhone(orderDetails),
  );

  useEffect(() => {
    const email = getCustomerEmail(orderDetails);
    const phone = getCustomerPhone(orderDetails);
    if (email) setEmailInput(email);
    if (phone) setPhoneInput(phone);
  }, [orderDetails?.customer?.email, orderDetails?.customer?.phone]);

  const handleToggleAction = (action) => {
    setExpandedAction((prev) => {
      const next = prev === action ? null : action;
      if (next === "whatsapp") {
        const phone = getCustomerPhone(orderDetails);
        if (phone && !phoneInput) setPhoneInput(phone);
      }
      if (next === "email") {
        const email = getCustomerEmail(orderDetails);
        if (email && !emailInput) setEmailInput(email);
      }
      return next;
    });
  };

  const customerName = useMemo(() => {
    const cust = orderDetails?.customer;
    if (!cust) return "Walk-in Customer";
    if (typeof cust === "string") return cust;
    return (
      cust?.full_name ||
      `${cust?.first_name || ""} ${cust?.last_name || ""}`.trim() ||
      "Walk-in Customer"
    );
  }, [orderDetails?.customer]);

  const invoiceRef = useMemo(() => {
    return (
      orderDetails?.invoice_number ||
      orderDetails?.reference ||
      orderDetails?.order_number ||
      (orderDetails?.id
        ? `INV-${String(orderDetails.id).padStart(4, "0")}`
        : "INV")
    );
  }, [orderDetails]);

  const dateDisplay = useMemo(() => {
    return defaultDateDisplay(
      orderDetails?.due_date || orderDetails?.created_at || new Date(),
    );
  }, [orderDetails]);

  const formattedAmount = useMemo(() => {
    return formatCurrency({
      value: orderDetails?.total_amount,
      currencyCode:
        orderDetails?.currency_code ||
        orderDetails?.store?.currency_code ||
        "NGN",
    });
  }, [orderDetails]);

  const activeInvoiceUrl = useMemo(() => {
    return (
      invoiceUrl || orderDetails?.invoice_url || orderDetails?.receipt_url || ""
    );
  }, [invoiceUrl, orderDetails]);

  const [activeAction, setActiveAction] = useState(null);

  const isPrintingInvoice = isGeneratingInvoice && activeAction === "print";
  const isDownloadingPdf = isGeneratingInvoice && activeAction === "download";

  const generateInvoice = ({ onSuccess, onSettled }) => {
    triggerGenerateInvoice({
      id: orderDetails?.id,
      bank_account_id: bankAccountIdToSend,
      onSuccess: ({ invoiceUrl: genUrl }) => {
        onSuccess(genUrl || orderDetails?.receipt_url || "");
      },
      onSettled: () => {
        if (onSettled) onSettled();
      },
    });
  };

  const isPhoneValid = useMemo(() => {
    if (!phoneInput) return false;
    const trimmed = String(phoneInput).trim();
    if (!/^\+?[0-9\s\-()]+$/.test(trimmed)) return false;
    const digits = trimmed.replace(/\D/g, "");
    return digits.length >= 9 && digits.length <= 15;
  }, [phoneInput]);

  const isEmailValid = useMemo(() => {
    if (!emailInput) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(emailInput).trim());
  }, [emailInput]);

  const handlePrintInvoice = () => {
    setActiveAction("print");
    generateInvoice({
      onSuccess: (url) => {
        if (url) printStandardInvoice(url);
        closeModal();
      },
      onSettled: () => {
        setActiveAction(null);
      },
    });
  };

  const handleDownloadPdf = () => {
    setActiveAction("download");
    generateInvoice({
      onSuccess: (url) => {
        if (url) window.open(url, "_blank");
        closeModal();
      },
      onSettled: () => {
        setActiveAction(null);
      },
    });
  };

  const handleShareWhatsApp = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!isPhoneValid) {
      triggerToast("Please enter a valid phone number", "warning");
      return;
    }
    const storeName = orderDetails?.store?.name || "our store";

    const sendToWhatsApp = (link) => {
      const message = `Hello${
        customerName !== "Walk-in Customer" ? ` ${customerName}` : ""
      },\nHere is your invoice (${invoiceRef}) from ${storeName}.${
        link
          ? `\nKindly click the link below to view or download your invoice PDF:\n${link}`
          : ""
      }`;

      const countryCode =
        orderDetails?.customer?.phone_code || business_phone_code || "234";

      const phone = formatWhatsAppPhone(phoneInput, countryCode);
      const query = new URLSearchParams({
        ...(phone && { phone }),
        text: message,
      });

      window.open(
        `https://api.whatsapp.com/send?${query.toString()}`,
        "_blank",
      );
      triggerToast("Redirecting to WhatsApp...", "success");
      closeModal();
    };

    if (!bankAccountIdToSend && activeInvoiceUrl) {
      sendToWhatsApp(activeInvoiceUrl);
    } else {
      generateInvoice({ onSuccess: sendToWhatsApp });
    }
  };

  const handleSendEmail = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const recipient = (emailInput || "").trim();

    if (!isEmailValid) {
      triggerToast("Please enter a valid email address", "warning");
      return;
    }

    sendOrderReceiptMutation.mutate(
      {
        medium: "email",
        send_to: recipient,
        order_id: orderDetails?.id,
      },
      {
        onSuccess: () => {
          triggerToast("Invoice sent successfully via email", "success");
          setExpandedAction(null);
          closeModal();
        },
      },
    );
  };

  return {
    customerName,
    invoiceRef,
    dateDisplay,
    formattedAmount,
    activeInvoiceUrl,
    bankAccounts,
    isLoadingBankAccounts,
    activeBankAccount,
    storeDefaultBankAccount,
    selectedBankAccountId,
    setSelectedBankAccountId,
    expandedAction,
    setExpandedAction,
    handleToggleAction,
    emailInput,
    setEmailInput,
    phoneInput,
    setPhoneInput,
    isPhoneValid,
    isEmailValid,
    handlePrintInvoice,
    handleDownloadPdf,
    handleShareWhatsApp,
    handleSendEmail,
    isGeneratingInvoice,
    isPrintingInvoice,
    isDownloadingPdf,
    isSendingEmail: sendOrderReceiptMutation?.isLoading,
  };
};

export default useInvoiceReady;
